export const MIN_PANE_PERCENTAGE = 25;
export const MAX_PANE_PERCENTAGE = 75;

/** The remembered size of the first pane when one pane splits in two. */
export function clampPanePercentage(percentage: number): number {
  if (!Number.isFinite(percentage)) return 50;
  return Math.max(MIN_PANE_PERCENTAGE, Math.min(MAX_PANE_PERCENTAGE, percentage));
}

/** Panes side by side ("row") or one above another ("column"). */
export type SplitDirection = "row" | "column";
export interface PaneSplit {
  split: SplitDirection;
  children: PaneLayout[];
  /** Each part's share of the split, in percent. */
  sizes: number[];
}
/** How the open panes fill the editor: one pane, by its index, or a split of two or more parts. */
export type PaneLayout = { pane: number } | PaneSplit;

/** Splitting stops at three panes across and three down, a 3×3 grid at most. */
export const MAX_PANES_ACROSS = 3;
export const MAX_PANES_DOWN = 3;

const isPane = (layout: PaneLayout): layout is { pane: number } => "pane" in layout;
const total = (sizes: readonly number[]) => sizes.reduce((sum, size) => sum + size, 0);

/** The panes in order, first to last: left to right and top to bottom within each split. */
export function layoutPanes(layout: PaneLayout): number[] {
  return isPane(layout) ? [layout.pane] : layout.children.flatMap(layoutPanes);
}

/** The most panes a line straight across the layout ("row") or straight down it ("column") meets. */
export function panesAlong(layout: PaneLayout, direction: SplitDirection): number {
  if (isPane(layout)) return 1;
  const counts = layout.children.map((child) => panesAlong(child, direction));
  return layout.split === direction ? total(counts) : Math.max(...counts);
}

/**
 * The layout with pane `added` beside `pane` ("row") or below it ("column"), where `pane`
 * keeps `share` percent of its space. A split the same way as the one `pane` is in joins
 * it, so three panes side by side are one split of three: parts that were even stay even,
 * and otherwise `pane` gives up half its space. Null when that would put more than three
 * panes across or down.
 */
export function splitLayout(
  layout: PaneLayout,
  pane: number,
  added: number,
  direction: SplitDirection,
  share = 50,
): PaneLayout | null {
  const split = (node: PaneLayout): PaneLayout => {
    if (isPane(node))
      return node.pane === pane
        ? { split: direction, children: [node, { pane: added }], sizes: [share, 100 - share] }
        : node;
    const at = node.children.findIndex((child) => isPane(child) && child.pane === pane);
    if (node.split !== direction || at === -1)
      return { split: node.split, children: node.children.map(split), sizes: [...node.sizes] };
    const children = [...node.children];
    children.splice(at + 1, 0, { pane: added });
    const even = node.sizes.every((size) => Math.abs(size - 100 / node.sizes.length) < 0.5);
    if (even) return { split: node.split, children, sizes: children.map(() => 100 / children.length) };
    const sizes = [...node.sizes];
    const size = sizes[at]!;
    sizes.splice(at, 1, (size * share) / 100, (size * (100 - share)) / 100);
    return { split: node.split, children, sizes };
  };
  const next = split(layout);
  return panesAlong(next, "row") > MAX_PANES_ACROSS || panesAlong(next, "column") > MAX_PANES_DOWN
    ? null
    : next;
}

/**
 * The layout without `pane`. The rest of its split share out its space in proportion,
 * and a split left with one part gives way to that part.
 */
export function closeLayout(layout: PaneLayout, pane: number): PaneLayout {
  const close = (node: PaneLayout): PaneLayout | null => {
    if (isPane(node)) return node.pane === pane ? null : node;
    const children: PaneLayout[] = [];
    const sizes: number[] = [];
    node.children.forEach((child, index) => {
      const kept = close(child);
      if (!kept) return;
      // What is left of a split the other way may now run the same way as this one.
      if (!isPane(kept) && kept.split === node.split)
        kept.children.forEach((part, at) => {
          children.push(part);
          sizes.push((node.sizes[index]! * kept.sizes[at]!) / 100);
        });
      else {
        children.push(kept);
        sizes.push(node.sizes[index]!);
      }
    });
    if (children.length < 2) return children[0] ?? null;
    const sum = total(sizes);
    return { split: node.split, children, sizes: sizes.map((size) => (size / sum) * 100) };
  };
  return close(layout) ?? layout;
}

/**
 * A layout read back from storage, or null unless splitting could have made it: each pane
 * once, splits of two or more parts with a positive size each, and three across and down
 * at most.
 */
export function parsePaneLayout(value: unknown): PaneLayout | null {
  const seen = new Set<number>();
  const valid = (node: unknown): node is PaneLayout => {
    if (typeof node !== "object" || node === null) return false;
    if ("pane" in node) {
      const { pane } = node;
      if (typeof pane !== "number" || !Number.isInteger(pane) || pane < 0
        || pane >= MAX_PANES_ACROSS * MAX_PANES_DOWN || seen.has(pane)) return false;
      seen.add(pane);
      return true;
    }
    const { split, children, sizes } = node as Partial<Record<keyof PaneSplit, unknown>>;
    return (split === "row" || split === "column")
      && Array.isArray(children) && children.length >= 2
      && Array.isArray(sizes) && sizes.length === children.length
      && sizes.every((size) => typeof size === "number" && Number.isFinite(size) && size > 0)
      && children.every(valid);
  };
  return valid(value) && panesAlong(value, "row") <= MAX_PANES_ACROSS
    && panesAlong(value, "column") <= MAX_PANES_DOWN ? value : null;
}

/** The pane after `pane`, going round to the first after the last. */
export function nextPane(layout: PaneLayout, pane: number): number {
  const order = layoutPanes(layout);
  return order[(order.indexOf(pane) + 1) % order.length]!;
}

/**
 * The pane next to `pane` in its split, the nearest one before it or else after it: where
 * the cursor goes when `pane` closes. Null when it is the only pane.
 */
export function paneBeside(layout: PaneLayout, pane: number): number | null {
  if (isPane(layout)) return null;
  const at = layout.children.findIndex((child) => isPane(child) && child.pane === pane);
  if (at === -1) {
    for (const child of layout.children) {
      const beside = paneBeside(child, pane);
      if (beside !== null) return beside;
    }
    return null;
  }
  return at > 0
    ? layoutPanes(layout.children[at - 1]!).at(-1)!
    : layoutPanes(layout.children[at + 1]!)[0]!;
}

/** The smallest share a part of a split can be dragged down to: half an even share, so 25% of two. */
export const minimumPaneShare = (parts: number) => 50 / parts;

/**
 * The sizes once the divider after part `index` moves to give that part `share` percent,
 * taken from or given to the part after it, as far as both stay usable.
 */
export function resizeSplit(sizes: readonly number[], index: number, share: number): number[] {
  const pair = sizes[index]! + sizes[index + 1]!;
  const least = Math.min(minimumPaneShare(sizes.length), pair / 2);
  const size = Number.isFinite(share) ? Math.max(least, Math.min(pair - least, share)) : pair / 2;
  const next = [...sizes];
  next[index] = size;
  next[index + 1] = pair - size;
  return next;
}

/** Where a dragged divider snaps, along its whole split, in percent: thirds and the half. */
export const SNAP_POINTS: readonly { at: number; label: string }[] = [
  { at: 100 / 3, label: "1/3" },
  { at: 50, label: "1/2" },
  { at: 200 / 3, label: "2/3" },
];

/**
 * The share to give part `index` for a divider dragged to give it `share`, and the point
 * it snapped to: a third or the half of the whole split when the divider is within `reach`
 * percent of one, and the split can be shared that way; otherwise `share` itself.
 */
export function snapShare(
  sizes: readonly number[],
  index: number,
  share: number,
  reach: number,
): { share: number; snap: string | null } {
  const start = total(sizes.slice(0, index));
  const pair = sizes[index]! + sizes[index + 1]!;
  const least = Math.min(minimumPaneShare(sizes.length), pair / 2);
  let snapped: { share: number; snap: string | null } = { share, snap: null };
  for (const point of SNAP_POINTS) {
    const at = point.at - start;
    const distance = Math.abs(at - share);
    if (distance > reach || at < least || at > pair - least) continue;
    if (snapped.snap === null || distance < Math.abs(snapped.share - share)) snapped = { share: at, snap: point.label };
  }
  return snapped;
}
